import { MouseEventHandler, useState } from "react";

import { ActionButton, Pill } from "~/components/ui";
import WaitTimeComponent from "./WaitTime";
import InfoItem from "./InfoItem";
import FormattedMinutes from "./FormattedMinutes";
import AlertInfo from "./AlertInfo";
import Star from "./Star";
import AlertBanner from "./AlertBanner";
import Tags from "./Tags";
import { isWaitTimeStale } from "../helpers/isWaitTimeStale";

import { ClientOnly } from "~/components/helpers/ClientOnly";

import { createDirectionsLink } from "~/utils";

import AnimateHeight from "react-animate-height";
import type { Height } from "react-animate-height";

import getDistance from "geolib/es/getDistance";

import { ParsedLocation } from "@edwt/cms/prisma";
import { audienceOptions } from "@edwt/cms/schema/fields/audience/options";
import {
  FaCompass,
  FaInfoCircle,
  FaMap,
  FaPhone,
  FaMapMarkerAlt,
  FaUserAlt,
  FaRegClock,
  FaRegBuilding,
} from "react-icons/fa";
import { FaInfo } from "react-icons/fa6";

import type { WaitTime } from ".prisma/client";
import clsx from "clsx";
import { useOperatingHours } from "~/hooks/useOperatingHours";
import { useStore } from "~/store";
import { useLocationOverrides } from "~/hooks/useLocationOverrides";

const Location = ({
  location,
  waitTime,
  lastUpdated,
  onClick,
  className,
  noJS = false,
}: {
  /** Parsed Location object with all its details. */
  location: ParsedLocation;
  /** Current Wait Time object. */
  waitTime: WaitTime | undefined;
  /** Last Updated Date. */
  lastUpdated: Date;
  /** Click handler for location (optional). */
  onClick?: MouseEventHandler<HTMLDivElement>;
  className?: string;
  /** Disable sub-elements that require Javascript (Star, Show More Info, etc) (optional, default is false). */
  noJS?: boolean;
}) => {
  // Re-parse date with the understanding that it is UTC
  const lastUpdatedUTC = lastUpdated
    ? new Date(lastUpdated?.toString())
    : undefined;

  const [moreInfoHeight, setMoreInfoHeight] = useState<Height>(
    noJS ? "auto" : 0,
  );

  const {
    overrideWaitTime,
    overrideAlertTitle,
    overrideAlertDescription,
    overrideAdditionalInfo,
    overrideOpenStatus,
  } = useLocationOverrides(location.overrideSchedule);

  const operatingHours = useOperatingHours(
    location.operatingHours,
    location.open247,
    overrideOpenStatus,
  );

  function toggleMoreInfo(event) {
    setMoreInfoHeight(moreInfoHeight === 0 ? "auto" : 0);

    if (event) stopPropagation(event);
  }

  const [isStarred, starLocation, unstarLocation, geocodeCache] = useStore(
    (state) => [
      state.starredLocations.find((x) => x === location.slug) ? true : false,
      state.starLocation,
      state.unstarLocation,
      state.geocodeCache,
    ],
  );

  function handleStarClick(event) {
    isStarred ? unstarLocation(location.slug) : starLocation(location.slug);

    stopPropagation(event);
  }

  function stopPropagation(event) {
    event.stopPropagation();
  }

  const userDistance =
    geocodeCache.userCoordinates &&
    geocodeCache.userCoordinates.long !== null &&
    geocodeCache.userCoordinates.lat !== null &&
    location.longitude !== null &&
    location.latitude !== null
      ? getDistance(
          {
            longitude: geocodeCache.userCoordinates.long,
            latitude: geocodeCache.userCoordinates.lat,
          },
          {
            longitude: location.longitude,
            latitude: location.latitude,
          },
        )
      : undefined;

  // Get text for additional info based on if the location is open or closed
  const additionalInfoText = location.showAdditionalInfoClosed
    ? operatingHours.isOpen
      ? location.additionalInfo
      : location.additionalInfoClosed
    : location.additionalInfo;

  const showAlert =
    (location.alertShow && location.alertTitle) || overrideAlertTitle;

  const showAlertDetails =
    showAlert && (location.alertDescription || overrideAlertDescription);

  const showAdditionalInfo = additionalInfoText || overrideAdditionalInfo;

  return (
    <div
      className={clsx(
        "group",
        {
          "focus:outline-2 focus:-outline-offset-1": onClick,
        },
        className,
      )}
      onClick={onClick}
      onKeyUp={(event) =>
        event.key === "Enter" ? onClick(undefined) : undefined
      }
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="px-3 py-3 @container xs:px-5 xs:py-4">
        {showAlert && (
          <AlertBanner
            text={overrideAlertTitle ? overrideAlertTitle : location.alertTitle}
            type={operatingHours.isOpen ? "warning" : "disabled"}
            className="mb-3 mt-0.5"
          />
        )}
        {/* Tags and Pin */}
        <div className="mb-1 flex">
          {/* Tags */}
          <Tags
            location={location}
            useOperatingHours={operatingHours}
            overrideOpenStatus={overrideOpenStatus}
          />
          {/* Star / Pin */}
          {noJS === false && (
            <div>
              <ClientOnly>
                <Star starred={isStarred} onClick={handleStarClick} />
              </ClientOnly>
            </div>
          )}
        </div>
        <div className="flex gap-4">
          <div className="flex-grow">
            {/* Titles */}
            <h3 className="py-1.5 text-xl font-bold leading-6">
              {location.name}
            </h3>

            {/* Above the fold info */}
            <ul className="mb-4 flex flex-col gap-0.5">
              <ClientOnly placeholder={noJS ? null : <>Loading</>}>
                {userDistance !== undefined && !noJS && (
                  <li>
                    <InfoItem icon={<FaMapMarkerAlt />}>
                      <span>{Math.round(userDistance / 1000)}km from you</span>
                    </InfoItem>
                  </li>
                )}
              </ClientOnly>
              <li>
                <InfoItem icon={<FaUserAlt />}>
                  {
                    audienceOptions.find((x) => x.value === location.audience)
                      ?.label
                  }
                </InfoItem>
              </li>
              {showAdditionalInfo && (
                <li>
                  <InfoItem icon={<FaInfo />}>
                    {overrideAdditionalInfo
                      ? overrideAdditionalInfo
                      : additionalInfoText}
                  </InfoItem>
                </li>
              )}
            </ul>
          </div>
          {/* Wait Time */}
          <div>
            <WaitTimeComponent
              waitTime={waitTime}
              useOperatingHours={operatingHours}
              showWaitTimes={location.showWaitTimes}
              showStatus={location.showStatus}
              location={location}
              overrideWaitTime={overrideWaitTime}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div
          className={clsx("grid gap-2 xs:mb-1", {
            "grid-cols-2": (location.type === "ed" && noJS) || noJS,
            "grid-cols-3": location.type === "ed" && !noJS,
            "grid-cols-4": location.type === "upcc" && !noJS,
          })}
        >
          <ActionButton
            title="Directions"
            icon={<FaMap />}
            target="_blank"
            href={createDirectionsLink("", location.address)}
            onClick={stopPropagation}
          />
          {location.type === "upcc" && !noJS && (
            <ActionButton
              title="Call"
              icon={<FaPhone />}
              href={location.phone ? `tel:${location.phone}` : undefined}
              onClick={stopPropagation}
              disabled={!location.phone || !operatingHours.isOpen}
            />
          )}
          <ActionButton
            title="Website"
            icon={<FaCompass />}
            href={location.website ?? undefined}
            target="_blank"
            onClick={stopPropagation}
            disabled={!location.website}
          />
          {noJS === false && (
            <ActionButton
              title={moreInfoHeight == "auto" ? "Hide Info" : "More Info"}
              icon={<FaInfoCircle />}
              onClick={toggleMoreInfo}
            />
          )}
        </div>

        {/* More Info */}
        <AnimateHeight
          duration={500}
          height={moreInfoHeight} // see props documentation below
        >
          <div className="flex flex-col gap-2 pt-3">
            {/* Below the fold info */}
            <ul className="">
              {waitTime &&
                location.showWaitTimes &&
                waitTime.elosMinutes &&
                operatingHours.isOpen &&
                !isWaitTimeStale(waitTime) && (
                  <li>
                    <InfoItem icon={<FaRegClock />}>
                      Estimated length of stay:{" "}
                      <FormattedMinutes minutes={waitTime.elosMinutes} />
                    </InfoItem>
                  </li>
                )}
              {location.phone && (
                <li>
                  <InfoItem icon={<FaPhone />}>{location.phone}</InfoItem>
                </li>
              )}
              {location.address && (
                <li>
                  <InfoItem icon={<FaRegBuilding />}>
                    {location.address}
                  </InfoItem>
                </li>
              )}
              {/* Emergency Department Description */}
              {location.description && (
                <li className="mt-3">
                  <p className="text-sm italic">{location.description}</p>
                </li>
              )}
              {/* Alert Description */}
              {showAlertDetails && (
                <li className="mt-3">
                  <AlertInfo
                    title={
                      overrideAlertTitle && overrideAlertDescription
                        ? overrideAlertTitle
                        : location.alertTitle
                    }
                    description={
                      overrideAlertTitle && overrideAlertDescription
                        ? overrideAlertDescription
                        : location.alertDescription
                    }
                  />
                </li>
              )}
              {/* Wait Times Last Updated Date */}
              {location.showWaitTimes && lastUpdatedUTC && (
                <li className="mt-3 text-sm text-gray" suppressHydrationWarning>
                  Last Updated {lastUpdatedUTC?.toLocaleString()}
                </li>
              )}
            </ul>
          </div>
        </AnimateHeight>
      </div>
    </div>
  );
};

export default Location;
